import {
  ParsedSkillResponse,
  parseSkillNameFromContent,
  SKILL_MD_PATH,
} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {LoadSkillToolCall} from 'llm/chat-library/tool-details/ParsedProgressToolCall';

/**
 * Parse the LoadSkill XML response into a ParsedSkillResponse shared with
 * ManageSkill rendering.
 *
 * The backend wraps skill content in pseudo-XML tags (see load_skill_tool.py).
 * We use simple regex extraction rather than DOMParser because the text nodes
 * may contain characters that break strict XML parsing (e.g. `<div>` in markdown).
 *
 * Frontmatter is intentionally preserved in `files[].content` — the renderer
 * (SkillMdBlock) extracts name/description into structured fields and shows
 * the body as markdown. For non-SKILL.md files the frontmatter is unlikely
 * to appear in practice.
 *
 * If you change anything here, you probably also need to update the tests in:
 * tests/unit/llm/tools/load_skill_tool_test.py
 */
export function parseLoadSkillResponse(
  rawResponse: string,
  toolCall: LoadSkillToolCall
): ParsedSkillResponse | null {
  const instructionsMatch = rawResponse.match(/<instructions>\n?([\s\S]*?)\n?<\/instructions>/);
  if (instructionsMatch) {
    const content = instructionsMatch[1].trim();
    return {
      success: true,
      skillId: toolCall.skillId || null,
      skillName: parseSkillNameFromContent(content) ?? toolCall.skillName ?? null,
      files: [{path: SKILL_MD_PATH, content}],
      message: '',
    };
  }

  const fileMatch = rawResponse.match(/<file\s+path="([^"]*)">\n?([\s\S]*?)\n?<\/file>/);
  if (fileMatch) {
    const path = fileMatch[1];
    const content = extractTextContent(fileMatch[2].trim());
    const isSkillMd = path === SKILL_MD_PATH;
    return {
      success: true,
      skillId: toolCall.skillId || null,
      skillName: isSkillMd
        ? (parseSkillNameFromContent(content) ?? toolCall.skillName ?? null)
        : toolCall.skillName || null,
      files: [{path, content}],
      message: '',
    };
  }

  return null;
}

/**
 * Extract the deepest text content from a file body.
 *
 * Non-plaintext files go through document extraction on the backend
 * (format_uploaded_file_for_llm), which wraps content in:
 *   <document><source>...</source><document-content><text-content>BODY</text-content>...</document-content></document>
 * Strip those wrappers so the popup shows only the actual file content.
 */
function extractTextContent(body: string): string {
  const textContentMatch = body.match(/<text-content>\n?([\s\S]*?)\n?<\/text-content>/);
  if (textContentMatch) {
    return textContentMatch[1].trim();
  }
  return body;
}

export function formatLoadSkillInput(toolCall: LoadSkillToolCall): string {
  const parts: Array<string> = [];
  if (toolCall.skillName) {
    parts.push(`Skill: ${toolCall.skillName}`);
  }
  if (toolCall.filePath) {
    parts.push(`File: ${toolCall.filePath}`);
  } else {
    parts.push(`File: SKILL.md`);
  }
  return parts.length > 0 ? parts.join('\n') : 'Loading skill instructions';
}
